import posthog from "posthog-js";

import type {
  TrainingItem,
  TrainingPersona,
  TrainingResponse,
} from "@/types/training";

export const PERSONA_ORDER: TrainingPersona[] = [
  "FRONT_DESK",
  "PROVIDER",
  "NURSE",
  "BILLING",
  "OWNER",
];

export const PERSONA_LABELS: Record<TrainingPersona, string> = {
  FRONT_DESK: "Front desk",
  PROVIDER: "Provider",
  NURSE: "Nurse / MA",
  BILLING: "Billing",
  OWNER: "Owner / Manager",
};

export interface TrainingSection {
  persona: TrainingPersona;
  label: string;
  items: TrainingItem[];
  openCount: number;
  isComplete: boolean;
}

/**
 * One section per role on the plan, main role first. A task several roles share sits
 * under the first of them, so it appears once.
 */
export function groupByPersona(data: TrainingResponse): TrainingSection[] {
  if (!data.plan) return [];
  const roles = [data.plan.persona, ...data.plan.extraPersonas];
  const placed = new Set<string>();
  return roles
    .map((persona) => {
      const items = data.items.filter(
        (item) => item.task.personas.includes(persona) && !placed.has(item.id),
      );
      items.forEach((item) => placed.add(item.id));
      const openCount = items.filter((i) => i.status === "OPEN").length;
      return {
        persona,
        label: PERSONA_LABELS[persona],
        items,
        openCount,
        isComplete: items.length > 0 && openCount === 0,
      };
    })
    .filter((section) => section.items.length > 0);
}

export function nextOpenTask(data: TrainingResponse): TrainingItem | null {
  for (const section of groupByPersona(data)) {
    const open = section.items.find((item) => item.status === "OPEN");
    if (open) return open;
  }
  return null;
}

/** Path part of a task route, for matching against the current page. */
export function routePath(route: string | null): string | null {
  return route?.split("?")[0].split("#")[0] ?? null;
}

export type TrainingSurface = "page" | "sidebar";

export type TrainingAction =
  | "show_me"
  | "go_there"
  | "docs"
  | "ask_decoda"
  | "skip"
  | "unskip"
  | "complete"
  | "uncomplete";

export function captureTrainingAction(
  item: TrainingItem,
  action: TrainingAction,
  surface: TrainingSurface,
) {
  posthog.capture("training_task_action", {
    action,
    surface,
    task: item.itemKey,
    status: item.status,
  });
}
